// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import { getTimesheetFieldLabel } from "../timesheet/timesheet-fields"
import { Code, from, Init, isConnectError, timelineService } from "../../../app/rpc"
import { ClearTimesheetFieldRequestSchema, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type ClearTimesheetFieldRpc = (request: Init<typeof ClearTimesheetFieldRequestSchema>) => Observable<TimeGroup>

export const clearTimesheetField = (clearTimesheetFieldRpc: ClearTimesheetFieldRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.clearTimesheetField.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => clearTimesheetFieldRpc(action.payload)
        .pipe(
          map(timeGroup => timeGroup),
          mergeMap((timeGroup) => concat(
            of(Slice.upsertTimeGroup(timeGroup)),
            of(Actions.generateTimesheetFieldConfigs([timeGroup])),
          )),
          catchError(e => {
            const timesheetField = state.timeline.timesheetFieldsCache[action.payload.timesheetFieldId ?? ""]
            const fieldName = timesheetField
              ? getTimesheetFieldLabel(timesheetField)
              : t({ id: "msg_timesheet_field", message: "Timesheet field" }).toLowerCase()
            return handleError(e, fieldName)
          }),
        )
      ),
    )

const handleError = (e: unknown, fieldName: string): Observable<Action> => {
  const actions: Action[] = []
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_clear_timesheet_field_not_found", 
            message: "Failed to clear {fieldName} value, {fieldName} or timesheet entry not found.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ 
          id: "msg_clear_timesheet_field_permission_denied", 
          message: "Failed to clear {fieldName} value. You do not have permission.",
          values: { fieldName },
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_clear_timesheet_field_failed_precondition", 
            message: "Failed to clear {fieldName} value. The timesheet entry has already been posted or is out of sync.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_clear_timesheet_field_failed",
      message: "Failed to clear {fieldName} value. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      values: { fieldName },
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const clearTimesheetFieldRpc = (request: Init<typeof ClearTimesheetFieldRequestSchema>): Observable<TimeGroup> =>
  from(() => timelineService.clearTimesheetField(create(ClearTimesheetFieldRequestSchema, request)))

export default clearTimesheetField(clearTimesheetFieldRpc)
